import { createContext, useContext, ReactNode } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { LivingWebsiteTierId } from '../lib/livingWebsiteProducts';
import type { RoboticsProductId, RoboticsVariantId } from '../lib/roboticsProducts';
import { INCUBATOR_CHECKOUT_KEY } from '../lib/incubatorProgram';

export type CheckoutProduct =
  | 'phone'
  | 'flash'
  | typeof INCUBATOR_CHECKOUT_KEY
  | RoboticsProductId
  | LivingWebsiteTierId;

export type CheckoutOptions = {
  variant?: RoboticsVariantId;
  /** Required for incubator checkout — validated server-side. */
  accessCode?: string;
};

interface StripeContextType {
  openCheckout: (product: CheckoutProduct, options?: CheckoutOptions) => Promise<void>;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    'pk_live_51N6cuWBL4xBXOGrq81HP95fIcPjlrjHpMcUhIdYCTBxGQmm3MjvvmFjLFhLx1aa3NtAlRPXZvc2rKUvKbQ7fM8WU00hRUKcP65'
);

const LIVING_WEBSITE_KEYS: LivingWebsiteTierId[] = [
  'living-website-lite',
  'living-website-pro',
];

const ROBOTICS_KEYS: RoboticsProductId[] = [
  'self-bot-x1',
  'self-bot-p1',
  'self-dog-p1',
  'self-dog-e1',
];

function isLivingWebsiteCheckout(product: CheckoutProduct): product is LivingWebsiteTierId {
  return LIVING_WEBSITE_KEYS.includes(product as LivingWebsiteTierId);
}

function isRoboticsCheckout(product: CheckoutProduct): product is RoboticsProductId {
  return ROBOTICS_KEYS.includes(product as RoboticsProductId);
}

export function StripeProvider({ children }: { children: ReactNode }) {
  const openCheckout = async (product: CheckoutProduct, options?: CheckoutOptions) => {
    const stripe = await stripePromise;
    if (!stripe) throw new Error('Stripe failed to load');

    let body: Record<string, string | number>;

    if (isLivingWebsiteCheckout(product)) {
      body = { checkoutKey: product };
    } else if (product === 'phone') {
      body = { checkoutKey: 'phone' };
    } else if (product === INCUBATOR_CHECKOUT_KEY) {
      body = { checkoutKey: INCUBATOR_CHECKOUT_KEY };
      if (options?.accessCode) {
        body.accessCode = options.accessCode;
      }
    } else if (isRoboticsCheckout(product)) {
      body = { checkoutKey: product };
      if (options?.variant) {
        body.variant = options.variant;
      }
    } else {
      const productId = import.meta.env.VITE_STRIPE_PRODUCT_ID_FLASH;

      if (!productId) {
        throw new Error(`Missing Stripe product ID for ${product}`);
      }

      body = {
        productId,
        priceAmount: 14900,
      };
    }

    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const { sessionId } = await response.json();
    const result = await stripe.redirectToCheckout({ sessionId });

    if (result.error) {
      throw new Error(result.error.message);
    }
  };

  return (
    <StripeContext.Provider value={{ openCheckout }}>{children}</StripeContext.Provider>
  );
}

export function useStripe() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}
